import { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  ActionCausedByRole,
  Participation,
  ParticipationHistoryEvent,
  translateIncentiveText,
  useIsAdmin,
  useParticipationHistoryEvents,
} from '@app/api';
import {
  Checkbox,
  EventHistory,
  HistoryEventDisplayConfigItem,
} from 'components';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  wrapper: {
    padding: theme.marginBase * 4,
    overflow: 'auto',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  filterContainer: {},
}));

interface Props {
  participation: Participation;
}

const eventConfig: Record<
  string,
  HistoryEventDisplayConfigItem<ParticipationHistoryEvent> | string
> = {
  ApplicationAccepted: {
    icon: 'ri-check-line',
    getDetails: (event, t) =>
      event.metadata.reason === 'QUOTA_VALIDATION'
        ? t('eventHistory.reason.acceptedByQuota', {
            context: event.actionCausedBy?.triggerEventName,
          })
        : undefined,
  },
  ApplicationDeclined: {
    icon: 'ri-close-line',
    getDetails: (event, t) =>
      event.metadata.reason === 'QUOTA_VALIDATION'
        ? t('eventHistory.reason.declinedByQuota', {
            context: event.actionCausedBy?.triggerEventName,
          })
        : undefined,
  },
  ApplicationMovedBackToValidation: 'ri-question-line',
  ApplicationRequireManualValidation: {
    icon: 'ri-question-line',
    getDetails: (event, t) =>
      event.metadata.reason === 'QUOTA_VALIDATION'
        ? t('eventHistory.reason.retrievedByQuota', {
            context: event.actionCausedBy?.triggerEventName,
          })
        : undefined,
  },
  ApplicationFormFinished: {
    icon: 'ri-list-check-2',
    anonymousCausedBy: ActionCausedByRole.PARTICIPANT,
  },
  ParticipantApplied: 'ri-file-user-line',
  ParticipantStartedApplying: {
    icon: 'ri-draft-line',
    anonymousCausedBy: ActionCausedByRole.PARTICIPANT,
  },
  ToValidateApplicationCreated: 'ri-question-line',

  ParticipantCommittedToDoTask: {
    icon: 'ri-calendar-check-line',
    getDetails: (event, t) =>
      t('eventHistory.appointmentDate', { date: event.metadata.date }),
  },
  TaskCancelled: {
    icon: 'ri-delete-bin-line',
    getContext: (event) =>
      event.metadata.isBackup
        ? 'backup'
        : event.metadata.lastMinute
        ? 'lastMinute'
        : undefined,
    getDetails: (event, t) =>
      event.metadata.date
        ? t('eventHistory.appointmentDate', { date: event.metadata.date })
        : undefined,
  },
  ParticipationTimedOutEvent: {
    icon: 'ri-time-line',
  },
  TaskCompleted: 'ri-calendar-check-line',
  TaskReported: 'ri-thumb-down-line',
  TaskValidated: 'ri-thumb-up-line',
  TaskRescheduled: {
    icon: 'ri-calendar-event-line',
    getDetails: (event, t) =>
      [
        t('eventHistory.previousAppointmentDate', {
          date: event.metadata.previousDate,
        }),
        t('eventHistory.appointmentDate', { date: event.metadata.newDate }),
      ].join('\n'),
  },
  TaskInstructionsChanged: 'ri-file-info-line',
  ParticipantStartedToDoTask: 'ri-play-line',
  ParticipantClickedOnLink: {
    icon: 'ri-cursor-fill',
    getDetails: (event, t) =>
      event.metadata?.code
        ? t('eventHistory.clickOnLinkError', event.metadata)
        : event.metadata.link,
  },

  OrderBilled: {
    icon: 'ri-coins-line',
    getContext: (event) => (event.metadata.withCredit ? 'CREDIT' : undefined),
    getDetails: (event) => event.metadata.invoiceId,
  },
  OrderCompleted: 'ri-coins-line',

  ClientAskedToPayParticipant: {
    icon: 'ri-coins-line',
    getDetails: (event, t) =>
      translateIncentiveText(t, {
        amount: event.metadata.incentive,
        currencyCode: event.metadata.currency,
      }) as string,
  },
  ClientCancelledAtLastMinute: {
    icon: 'ri-coins-line',
    getDetails: (event, t) =>
      translateIncentiveText(t, {
        amount: event.metadata.incentive,
        currencyCode: event.metadata.currency,
      }) as string,
  },
  MarkedAsPayedByAdmin: 'ri-coins-line',

  EmailSent: {
    icon: 'ri-mail-send-line',
    getContext: (event) => event.metadata.specialDest,
    getDetails: (event, t) =>
      t(`eventHistory.emailTemplates.${event.metadata.templateName}`),
  },
  EmailAccepted: {
    icon: 'ri-mail-line',
    getContext: (event) => event.metadata.specialDest,
    getDetails: (event, t) =>
      t(`eventHistory.emailTemplates.${event.metadata.templateName}`),
  },
  EmailClicked: {
    icon: 'ri-cursor-fill',
    getContext: (event) => event.metadata.specialDest,
    getDetails: (event, t) =>
      [
        t(`eventHistory.emailTemplates.${event.metadata.templateName}`),
        event.metadata.linkClicked,
      ]
        .filter(Boolean)
        .join('\n'),
  },
  EmailDelivered: {
    icon: 'ri-mail-check-line',
    getContext: (event) => event.metadata.specialDest,
    getDetails: (event, t) =>
      t(`eventHistory.emailTemplates.${event.metadata.templateName}`),
  },
  EmailDeliveryFailed: {
    icon: 'ri-mail-forbid-line',
    getContext: (event) => event.metadata.specialDest,
    getDetails: (event, t) =>
      [
        t(`eventHistory.emailTemplates.${event.metadata.templateName}`),
        event.metadata.reason,
      ]
        .filter(Boolean)
        .join('\n'),
  },
  EmailOpened: {
    icon: 'ri-mail-open-line',
    getContext: (event) => event.metadata.specialDest,
    getDetails: (event, t) =>
      t(`eventHistory.emailTemplates.${event.metadata.templateName}`),
  },
  EmailRequested: {
    icon: 'ri-mail-line',
    getContext: (event) => event.metadata.specialDest,
    getDetails: (event, t) =>
      t(`eventHistory.emailTemplates.${event.metadata.templateName}`),
  },
  EmailSendingError: {
    icon: 'ri-mail-forbid-line',
    getContext: (event) => event.metadata.specialDest,
    getDetails: (event, t) =>
      [
        t(`eventHistory.emailTemplates.${event.metadata.templateName}`),
        event.metadata.reason,
      ]
        .filter(Boolean)
        .join('\n'),
  },

  ParticipantInvited: 'ri-mail-line',
  NewMessageEvent: 'ri-message-line',
  ParticipantNotAvailableEvent: 'ri-calendar-check-line',
  NewEvaluation: {
    icon: 'ri-star-line',
    getDetails: (event, t) =>
      [
        t('eventHistory.newEvaluation.score', { score: event.metadata.score }),
        t('eventHistory.newEvaluation.message', {
          message: event.metadata.message,
          b: <b></b>,
        }),
      ].join('\n'),
  },
  ParticipantRedirectedToProvider: {
    icon: 'ri-reply-line',
    getDetails: (event) => event.metadata.redirectionLink,
  },
  ParticipantLandedOnLink: {
    icon: 'ri-link-m',
    getDetails: (event, t) =>
      [
        t('eventHistory.landedOnLink', event.metadata),
        event.metadata.reportReason
          ? t(
              `sessions.details.monitoring.meetingRating.participationReport.participationReportReason.${event.metadata.reportReason}`,
            )
          : undefined,
      ]
        .filter(Boolean)
        .join('\n'),
  },
  SmsSent: {
    icon: 'ri-phone-line',
    getDetails: (event) => event.metadata.text,
  },
  SmsAccepted: {
    icon: 'ri-phone-line',
    getDetails: (event) => event.metadata.text,
  },
  SmsFailed: {
    icon: 'ri-phone-line',
    getDetails: (event) => event.metadata.text,
  },
  SmsReady: {
    icon: 'ri-phone-line',
    getDetails: (event) => event.metadata.text,
  },
  SmsRequested: {
    icon: 'ri-phone-line',
    getDetails: (event) => event.metadata.text,
  },
};

const notImportantEvents = [
  'ApplicationFormFinished',
  'OrderCompleted',
  'ParticipantStartedToDoTask',
  'EmailAccepted',
  'EmailOpened',
  'EmailRequested',
  'EmailDelivered',
  'ParticipantRedirectedToProvider',
  'SmsRequested',
  'SmsReady',
  'SmsAccepted',
];

export const HistoryTab = ({ participation }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isAdmin = useIsAdmin();
  const { data: events } = useParticipationHistoryEvents(participation.id);
  const [showOnlyImportant, setShowOnlyImportant] = useState(true);
  const toggleOnlyImportant = useCallback(
    () => setShowOnlyImportant((s) => !s),
    [setShowOnlyImportant],
  );

  if (!events) {
    return null;
  }

  const filteredEvents = showOnlyImportant
    ? events.filter((e) => !notImportantEvents.includes(e.eventName))
    : events;

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        {isAdmin && (
          <div className={classes.filterContainer}>
            <Checkbox
              label={t('eventHistory.showOnlyImportant')}
              checked={showOnlyImportant}
              onChange={toggleOnlyImportant}
            />
          </div>
        )}
      </div>
      <EventHistory events={filteredEvents} eventsConfig={eventConfig} />
    </div>
  );
};
